import React, { useCallback, useMemo } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";

import { RoleName } from "constants";
import type {
  IClientEntitiesTableRowProps,
  IEntity
} from "./EntitiesTab.types";
import { AllocationCellEditor } from "./AllocationCell";

// ------ Table Header -----------------------------------------------

const HEADER_CELLS = [
  "Entity name",
  "Allocation %",
  "Linked deals",
  "Actions"
] as const;

// ------ Color tokens -----------------------------------------------

const PILL_STYLE = {
  bgcolor: "#b2eaad",
  color: "#013951",
  fontSize: "16px",
  fontWeight: 600,
  width: "96px",
  py: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px"
} as const;

const ACTION_ICON = {
  width: "24px",
  height: "24px",
  color: "#5e7a95",
  "&:hover": { bgcolor: "#f5f7f7" }
} as const;

// ------ Row helpers -----------------------------------------------

/**
 * Props extended by the parent component for inline editing.
 */
interface IClientEntityRowExtensions {
  /** ID of the entity currently in edit mode (from parent state). */
  editingEntityId: number | null;
  /** Callback to open edit mode for a given entity (from parent). */
  onEditOpen: (entityId: number) => void;
}

/**
 * A single entity data row, rendered as a <TableRow>.
 *
 * Edit mode is controlled by the parent EntitiesTab so that the
 * mutation lifecycle (onSuccess / onError) manages when the cell goes
 * back to read-only mode.
 */
const ClientEntityRow: React.FC<
  IClientEntitiesTableRowProps &
    { isTeamViewer: boolean } &
    IClientEntityRowExtensions
> = ({
  entity,
  roleId,
  isTeamViewer,
  editingEntityId,
  onEditOpen,
  onAllocationUpdate,
  onRemoveEntity,
  onEditCancel
}) => {
  const entityName = useMemo(() => entity.investor_name ?? "", [entity.investor_name]);

  // Whether this row is currently the one being edited.
  const entityMatchesEditing = useMemo(
    () => editingEntityId === entity.id,
    [editingEntityId, entity.id]
  );

  // Whether actions are visible (Admin or Team Member).
  const canManage = useMemo(
    () =>
      roleId !== null &&
      (roleId === RoleName.Admin || roleId === RoleName.Analyst),
    [roleId]
  );

  // ---- Handlers ------------------------------------------------

  const handleEditClick = useCallback(() => {
    onEditOpen(entity.id);
  }, [entity.id, onEditOpen]);

  const handleAllocationUpdate = useCallback(
    (newPercentage: number) => {
      onAllocationUpdate(entity.id, newPercentage);
    },
    [entity.id, onAllocationUpdate]
  );

  const handleEditCancel = useCallback(() => {
    onEditCancel();
  }, [onEditCancel]);

  const handleRemoveClick = useCallback(() => {
    onRemoveEntity(entity.id);
  }, [entity.id, onRemoveEntity]);

  // ---- Render ------------------------------------------------

  return (
    <TableRow
      key={entity.id}
      sx={{ "&:hover": { bgcolor: "action.hover" } }}
    >
      {/* Entity name + vehicle type + shared indicator */}
      <TableCell
        sx={{
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
          padding: "0 20px",
          color: "#0a3142",
          fontSize: "16px",
          height: "80px"
        }}
      >
        <Tooltip title={entityName} arrow>
          <Stack>
            <Typography
              sx={{
                fontSize: "16px",
                color: "#0a3142",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {entityName}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#898989" }}>
              {entity.entity_type ?? "\u2014"}
              {entity.is_shared ? " Shared" : ""}
            </Typography>
          </Stack>
        </Tooltip>
      </TableCell>

      {/* Allocation % — either a pill or inline editor */}
      {entityMatchesEditing ? (
        <AllocationCellEditor
          key={`${entity.id}-edit`}
          initialValue={entity.allocation_percentage}
          roleId={roleId}
          onUpdate={handleAllocationUpdate}
          onCancel={handleEditCancel}
        />
      ) : (
        <TableCell
          align="center"
          sx={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            padding: "0 20px",
            color: "#0a3142",
            fontSize: "16px",
            height: "80px"
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%", height: "100%" }}
          >
            <Typography sx={PILL_STYLE}>
              {entity.allocation_percentage}%
            </Typography>
          </Stack>
        </TableCell>
      )}

      {/* Linked deals count */}
      <TableCell
        align="center"
        sx={{
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
          padding: "0 20px",
          color: "#0a3142",
          fontSize: "16px",
          height: "80px"
        }}
      >
        {entity.linked_deals_count}
      </TableCell>

      {/* Actions — row-hover fade */}
      {!isTeamViewer && (
        <TableCell
          align="center"
          sx={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            padding: "0 20px",
            color: "#0a3142",
            fontSize: "16px",
            height: "80px"
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: "center",
              gap: 0.5,
              opacity: canManage ? 1 : 0,
              transition: "opacity 0.2s",
              pointerEvents: canManage ? "auto" : "none"
            }}
          >
            {/* Edit — restricted to Admin / Team Member */}
            {canManage && (
              <IconButton onClick={handleEditClick} sx={ACTION_ICON}>
                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            )}

            {/* Remove — restricted to Admin / Team Member */}
            {canManage && (
              <IconButton onClick={handleRemoveClick} sx={ACTION_ICON}>
                <DeleteOutlineIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            )}
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
};

// ------ Table Container -----------------------------------------------

/**
 * ClientEntitiesTable — a complete MUI data-table for the Entities tab.
 *
 * Columns (in order): Entity name, Allocation %, Linked deals, Actions.
 * No "Effective from" column per requirements.
 */
export const ClientEntitiesTable: React.FC<{
  entities: IEntity[];
  roleId: string | null;
  editingEntityId: number | null;
  onAllocationUpdate: (entityId: number, newPercentage: number) => void;
  onRemoveEntity: (entityId: number) => void;
  onEditCancel: () => void;
  onEditOpen: (entityId: number) => void;
}> = ({
  entities,
  roleId,
  editingEntityId,
  onAllocationUpdate,
  onRemoveEntity,
  onEditCancel,
  onEditOpen
}) => {
  const isTeamViewer = useMemo(
    () => roleId === RoleName.TeamViewer,
    [roleId]
  );

  const headers = useMemo(
    () =>
      isTeamViewer
        ? HEADER_CELLS.filter((h) => h !== "Actions")
        : HEADER_CELLS,
    [isTeamViewer]
  );

  const memoizedOnAllocationUpdate = useCallback(
    (entityId: number, newPercentage: number) => {
      onAllocationUpdate(entityId, newPercentage);
    },
    [onAllocationUpdate]
  );

  const memoizedOnRemoveEntity = useCallback(
    (entityId: number) => {
      onRemoveEntity(entityId);
    },
    [onRemoveEntity]
  );

  const memoizedOnEditCancel = useCallback(() => {
    onEditCancel();
  }, [onEditCancel]);

  const memoizedOnEditOpen = useCallback(
    (entityId: number) => {
      onEditOpen(entityId);
    },
    [onEditOpen]
  );

  return (
    <TableContainer
      sx={{ borderRadius: "10px", border: "1px solid #dae0e2", height: "100%", overflow: "auto" }}
    >
      <Table sx={{ minWidth: 900, color: "#0a3142" }}>
        {/* Table header */}
        <TableHead>
          <TableRow sx={{ backgroundColor: "#edf1f9" }}>
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{
                  fontSize: "15px",
                  fontWeight: "normal",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  padding: "0 20px",
                  color: "#0a3142",
                  height: "50px"
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* Table body */}
        <TableBody>
          {entities.map((entity) => (
            <ClientEntityRow
              key={entity.id}
              entity={entity}
              roleId={roleId}
              isTeamViewer={isTeamViewer}
              editingEntityId={editingEntityId}
              onEditOpen={memoizedOnEditOpen}
              onAllocationUpdate={memoizedOnAllocationUpdate}
              onRemoveEntity={memoizedOnRemoveEntity}
              onEditCancel={memoizedOnEditCancel}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
